import { InputNumber } from "@/components/InputNumber";
import { rangePickerFormItemProps, selectStandardProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { AgentType } from "../../../agent/enums";
import { useHasPermissions } from "../../../auth/hooks";
import AffiliatePartnerSelect from "../../../enumerations/components/form/AffiliatePartnerSelect";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { CommissionsBatchesTreeSelect } from "../../../enumerations/components/form/CommissionsBatchesTreeSelect";
import { CommoditySavingsPlan, contractDateMonthsTMap, ContractOrigin, InvestmentFeeType } from "../../enums";
import { ContractFilterPageRequest } from "../../types";
import { AgentExtendedFilterModal } from "./AgentExtendedFilterModal";

type Props = {
  hidden?: boolean;
};

export const ContractExtendedFilterFormFields = ({ hidden }: Props) => {
  const { t } = useTranslation();

  const [isSignerExtendedFilterOpen, setIsSignerExtendedFilterOpen] = useState(false);
  const [isManagerExtendedFilterOpen, setIsManagerExtendedFilterOpen] = useState(false);

  const form = Form.useFormInstance<ContractFilterPageRequest>();
  const annualPremiumOrApprovedAmountMin = Form.useWatch("annualPremiumOrApprovedAmountMin", form) || 1;

  const hasContractVerificationExportPermission = useHasPermissions(Permission.CONTRACT_VERIFICATION_EXPORT);

  return (
    <>
      <div style={{ display: hidden ? "none" : "block", position: "relative" }}>
        <Form.Item name="signerIds" label={t("contract.filter.signerIds")} hidden={hidden}>
          <AgentSelect
            optionsProps={{ agentTypes: [AgentType.NATURAL, AgentType.SELF_EMPLOYED] }}
            allowClear
            isMultiple
            onExtended={() => setIsSignerExtendedFilterOpen(true)}
          />
        </Form.Item>

        <AgentExtendedFilterModal
          key="signer"
          isOpen={isSignerExtendedFilterOpen}
          onCancel={() => setIsSignerExtendedFilterOpen(false)}
          onSubmit={(values) => {
            form.setFieldsValue(values);
            setIsSignerExtendedFilterOpen(false);
          }}
          fieldNames={{
            allSubordinateIdFieldName: "signerAllSubordinateId",
            directSubordinateIdFieldName: "signerDirectSubordinateId",
            directorateIdsFieldName: "signerDirectorateIds",
          }}
        />
      </div>

      <div style={{ display: hidden ? "none" : "block", position: "relative" }}>
        <Form.Item name="managerIds" label={t("contract.filter.managerIds")} hidden={hidden}>
          <AgentSelect
            optionsProps={{ agentTypes: [AgentType.NATURAL, AgentType.SELF_EMPLOYED] }}
            allowClear
            isMultiple
            onExtended={() => setIsManagerExtendedFilterOpen(true)}
          />
        </Form.Item>

        <AgentExtendedFilterModal
          key="manager"
          isOpen={isManagerExtendedFilterOpen}
          onCancel={() => setIsManagerExtendedFilterOpen(false)}
          onSubmit={(values) => {
            form.setFieldsValue(values);
            setIsManagerExtendedFilterOpen(false);
          }}
          fieldNames={{
            allSubordinateIdFieldName: "managerAllSubordinateId",
            directSubordinateIdFieldName: "managerDirectSubordinateId",
            directorateIdsFieldName: "managerDirectorateIds",
          }}
        />
      </div>

      <Form.Item
        name="createdAtRange"
        label={t("contract.filter.createdAtRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          maxDate={dayjs()}
          presets={getDatePickerDefaultRanges({ hasToday: true, maxDate: dayjs() })}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item name="createdByKeyword" label={t("contract.filter.createdByKeyword")} hidden={hidden}>
        <Input allowClear placeholder={t("contract.filter.helpers.userKeywordHint")} />
      </Form.Item>

      <Form.Item
        name="verifiedAtRange"
        label={t("contract.filter.verifiedAtRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          maxDate={dayjs()}
          presets={getDatePickerDefaultRanges({ hasToday: true, maxDate: dayjs() })}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item name="verifiedByKeyword" label={t("contract.filter.verifiedByKeyword")} hidden={hidden}>
        <Input allowClear placeholder={t("contract.filter.helpers.userKeywordHint")} />
      </Form.Item>

      <Form.Item
        name="mediationReportSignDateRange"
        label={t("contract.filter.mediationReportSignDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <AffiliatePartnerSelect
        formItemProps={{
          name: "affiliatePartnerIds",
          label: t("contract.filter.affiliatePartners"),
          hidden,
        }}
        selectProps={{
          mode: "multiple",
          maxTagCount: "responsive",
          allowClear: true,
        }}
      />

      <Form.Item
        name="effectiveBeginningDateRange"
        label={t("contract.filter.effectiveBeginningDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item
        name="signDateRange"
        label={t("contract.filter.signDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item
        name="effectiveBeginningDateMonths"
        label={t("contract.filter.effectiveBeginningDateMonths")}
        hidden={hidden}
      >
        <Select
          {...selectTagsStandardProps(contractDateMonthsTMap)}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          options={Array.from({ length: 12 }, (_, i) => i + 1).map((month) => ({
            label: t(`common.months.${month}`),
            value: month,
          }))}
        />
      </Form.Item>

      <Form.Item name="signDateMonths" label={t("contract.filter.signDateMonths")} hidden={hidden}>
        <Select
          {...selectTagsStandardProps(contractDateMonthsTMap)}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          options={Array.from({ length: 12 }, (_, i) => i + 1).map((month) => ({
            label: t(`common.months.${month}`),
            value: month,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="effectiveEndDateOrLoanMaturityDateRange"
        label={t("contract.filter.effectiveEndDateOrLoanMaturityDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item
        name="cancellationDateRange"
        label={t("contract.filter.cancellationDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item name="investmentFeeTypes" label={t("contract.filter.investmentFeeTypes")} hidden={hidden}>
        <Select
          {...selectStandardProps}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          options={Object.values(InvestmentFeeType).map((feeType) => ({
            label: t(`contract.enums.investmentFeeType.${feeType}`),
            value: feeType,
          }))}
        />
      </Form.Item>

      <Form.Item name="commoditySavingsPlans" label={t("contract.filter.commoditySavingsPlans")} hidden={hidden}>
        <Select
          {...selectStandardProps}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          options={Object.values(CommoditySavingsPlan).map((feeType) => ({
            label: t(`contract.enums.commoditySavingsPlan.${feeType}`),
            value: feeType,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="transferredToOtherBrokerDateRange"
        label={t("contract.filter.transferredToOtherBrokerDateRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          presets={getDatePickerDefaultRanges()}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item
        name="transferredFromOtherBroker"
        valuePropName="checked"
        className="margin-top-medium"
        rules={[validations.none]}
        hidden={hidden}
      >
        <Checkbox>{t("contract.filter.transferredFromOtherBroker")}</Checkbox>
      </Form.Item>

      <Form.Item
        name="vehicleFirstRegistrationYearRange"
        label={t("contract.filter.vehicleFirstRegistrationYearRange")}
        {...rangePickerFormItemProps("year")}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          picker="year"
          format="YYYY"
          maxDate={dayjs()}
          defaultPickerValue={[dayjs().subtract(12, "year"), dayjs().subtract(6, "year")]}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <div style={{ display: hidden ? "none" : "block" }}>
        <div style={{ paddingBottom: 8 }}>{t("contract.filter.annualPremiumOrApprovedAmountRange")}</div>
        <Flex gap={rowGutter}>
          <Form.Item name="annualPremiumOrApprovedAmountMin" rules={[validations.minNumber(1)]} hidden={hidden}>
            <InputNumber addon="euro" placeholder={t("common.from")} />
          </Form.Item>

          <Form.Item
            name="annualPremiumOrApprovedAmountMax"
            rules={[validations.minNumber(annualPremiumOrApprovedAmountMin)]}
            hidden={hidden}
          >
            <InputNumber addon="euro" placeholder={t("common.to")} />
          </Form.Item>
        </Flex>
      </div>

      <Form.Item name="origins" label={t("contract.enums.contractOrigin._label")} hidden={hidden}>
        <Select
          {...selectStandardProps}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          options={Object.values(ContractOrigin).map((origin) => ({
            label: t(`contract.enums.contractOrigin.${origin}`),
            value: origin,
          }))}
        />
      </Form.Item>

      <Form.Item name="commissionBatchIds" label={t("contract.filter.commissionBatchIds")} hidden={hidden}>
        <CommissionsBatchesTreeSelect />
      </Form.Item>

      {hasContractVerificationExportPermission ? (
        <Form.Item
          name="onlyIncludedInVerificationExport"
          valuePropName="checked"
          rules={[validations.none]}
          hidden={hidden}
        >
          <Checkbox>{t("contract.filter.onlyIncludedInVerificationExport")}</Checkbox>
        </Form.Item>
      ) : undefined}
    </>
  );
};
